import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight } from "lucide-react";
import { TEXT_GUIDES } from "@/data/textGuides";

export default function TextGuidesIndexPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "VCE English text guides",
    description: "Free VCE analysis of the English and EAL set texts we teach in Melbourne.",
    hasPart: TEXT_GUIDES.map((g) => ({
      "@type": "Article",
      headline: `${g.title} VCE analysis`,
      url: `${typeof window !== "undefined" ? window.location.origin : ""}/guides/${g.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="text-guides-index">
      <Seo title="VCE English Text Guides | Free Analysis & Themes" rawTitle
        description="Free VCE analysis of the English and EAL set texts we teach: themes, structure, essay approaches and the first pages of each in-house study guide."
        path="/guides" jsonLd={jsonLd} />
      <Navbar />

      <section className="pt-28 pb-10 px-6 bg-gradient-to-b from-brand-purple-soft/40 to-white dark:from-brand-purple/10 dark:to-[#0f1117]">
        <Reveal className="max-w-3xl mx-auto text-center">
          <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full">Free to read</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight mb-5">
            VCE English text guides
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300 font-body leading-relaxed">
            Written in-house for the texts we actually teach — the themes that earn marks, how to write about each
            one, and the first pages of every study guide, free and without an email wall.
          </p>
        </Reveal>
      </section>

      <section className="py-14 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TEXT_GUIDES.map((g, i) => (
            <Reveal key={g.slug} delay={(i % 3) * 80}>
              <Link to={`/guides/${g.slug}`}
                className="group block h-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 hover:border-brand-purple/50 hover:shadow-lg transition-all"
                data-testid={`guide-card-link-${g.slug}`}>
                <img src={`/guides/${g.slug}.jpg`} alt={`${g.title} study guide cover`} loading="lazy"
                  className="w-full aspect-[4/3] object-cover object-top bg-slate-100" />
                <div className="p-5">
                  <p className="text-xs font-body text-brand-purple dark:text-brand-purple-light font-semibold mb-1.5">
                    {g.form} · {g.level}
                  </p>
                  <h2 className="font-heading text-lg font-bold text-slate-900 dark:text-white leading-snug mb-1 group-hover:text-brand-purple transition-colors">
                    {g.title}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-body mb-3">{g.author}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-body font-semibold text-brand-purple dark:text-brand-purple-light">
                    Read the analysis <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
